import { B2Tabs } from "@hdb/biome-2";
import { useQuery, useQueryClient, QueryFunctionContext } from "@tanstack/react-query";
import { CSSProperties, FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import HeatmapHighchartComponent from "src/shared/custom-highchart-component/heatmap-highchart.component";
import {
  getassetDisplayLevelAndLocationsByBlock,
  getAssetDisplayLevelsByBlock,
} from "src/store/slices/dashboard.slice";
import { getBaselineListByModuleId } from "../../../../../core/api/services/baseline.service";
import {
  getMotionHeatmapData,
  getMotionHeatMapDataByBlockCodeWithLevelLocation,
} from "../../../../../core/api/services/dashbaorad-details/motion-heatmap.service";
import {
  AssetDisplayLevelChartType,
  MotionHeatmapChartType,
} from "../../../../../core/enums/dashboard-enums";
import CustomSpinnerComponent from "../../../../../shared/custom-spinner/custom-spinner.component";
import {
  DEFAULT_TICK_POSITION,
  HeatmapModel,
} from "../../../../../shared/models/heatmap.model";
import "../dashboard-details.component.scss";
import TabularFilterComponent from "../extra-components/tabular.filter.component";
import { generateFilenameWithTimestamp } from "src/shared/helpers/generateFilenameWithTimestamp";
import {
  modifyDataForHeatmapLevel,
  modifyDataForHeatmapLocation,
  modifyDataForHeatmapSensor,
} from "src/shared/helpers/modify-data-for-heatmap";

interface MotionHeatmapChartTypes {
  blockCode: any;
  isRefreshed: boolean;
}

const MotionHeatmapChart: FC<MotionHeatmapChartTypes> = ({
  blockCode,
  isRefreshed,
}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const [switchMotionHeatmapChartView, setSwitchMotionHeatmapChartView] =
    useState<number>(MotionHeatmapChartType.Motion_Count_Vs_Level);

  const [extraSensorData, setExtraSensorData] = useState<any>([]);
  const [isBaselineLoading, setIsBaselineLoading] = useState(true);

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [activeKey, setActiveKey] = useState("Level");
  const [render, setRender] = useState(0);

  const [assetDisplayLevelsByBlockData, setAssetDisplayLevelsByBlockData] = useState<any>(null);
  const [isLoadingAssetDisplayLevelAndLocationsByBlock, setIsLoadingAssetDisplayLevelAndLocationsByBlock] = useState<boolean>(true);

  const [tickPositions, setTickPositions] = useState<string[]>([]);

  const [redirectChart, setRedirectChart] = useState<string | null>("LEVEL");

  const isSensorView =
    switchMotionHeatmapChartView ===
    MotionHeatmapChartType.Motion_Count_Vs_Sensor;

  const shouldWaitForLevels =
    isSensorView && !!selectedLocation;

  const isLevelDataReady =
    !isLoadingAssetDisplayLevelAndLocationsByBlock &&
    !!assetDisplayLevelsByBlockData;

  // New API call to fetch motion heatmap data with level or location
  const {
    data: motionHeatmapApiData,
    isLoading: isLoadingMotionHeatmapApiData,
  } = useQuery({
    queryKey: [
      "getMotionHeatMapDataByBlockCodeWithLevelLocation",
      blockCode,
      switchMotionHeatmapChartView,
      selectedLocation,
      redirectChart,
    ],
    queryFn: ({ signal }: QueryFunctionContext) => {

      const location = isSensorView && selectedLocation ? selectedLocation : null;

      const assetDisplayLevelsByBlockDataForSelectedLocation = redirectChart !== null ? assetDisplayLevelsByBlockData?.filter(
        (item: any) => item?.Type === redirectChart
      ) : assetDisplayLevelsByBlockData;

      const locationType =
        shouldWaitForLevels
          ? assetDisplayLevelsByBlockDataForSelectedLocation?.find(
            (item: any) => item.DisplayLevel === selectedLocation
          )?.Type ?? null
          : null;

      return getMotionHeatMapDataByBlockCodeWithLevelLocation(
        blockCode,
        switchMotionHeatmapChartView,
        location,
        locationType,
        signal
      );
    },
    enabled:
      !!blockCode &&
      (
        // ✅ CASE 1: no selectedLocation → call immediately
        !shouldWaitForLevels ||

        // ✅ CASE 2: selectedLocation exists → wait for levels API
        isLevelDataReady
      ),
  });

  const motionHeatmapSensorData: HeatmapModel = useMemo(() => {
    if (
      motionHeatmapApiData &&
      switchMotionHeatmapChartView ===
      MotionHeatmapChartType.Motion_Count_Vs_Sensor
    ) {
      const extractedData = motionHeatmapApiData?.map((item: any) => [
        item?.sensorDisplayName,
        item?.hour,
        item?.motionCount,
        item?.displayLocationName,
        item?.assetCode,
      ]);

      const { identicalStringsList, newData, tickPositions, locationList } =
        modifyDataForHeatmapSensor(extractedData);

      const extraData = motionHeatmapApiData
        .filter(
          (d: any, index: number, array: any[]) =>
            array.findIndex((item: any) => item.assetCode === d.assetCode) ===
            index
        )
        .map((d: any) => ({
          code: d.assetCode,
          displayName: d.sensorDisplayName,
          level: d.location,
        }));

      setExtraSensorData(extraData);
      setTickPositions(tickPositions || DEFAULT_TICK_POSITION);

      return { identicalStringsList, newData, locationList };
    }
    return { identicalStringsList: [], newData: [], locationList: [] };
  }, [motionHeatmapApiData, switchMotionHeatmapChartView]);

  const motionHeatmapLevelData: HeatmapModel = useMemo(() => {
    if (
      motionHeatmapApiData &&
      switchMotionHeatmapChartView ===
      MotionHeatmapChartType.Motion_Count_Vs_Level
    ) {
      const extractedData = motionHeatmapApiData.map((item: any) => [
        item.location,
        item.hour,
        item.motionCount,
      ]);

      const { identicalStringsList, newData, tickPositions } =
        modifyDataForHeatmapLevel(extractedData);

      setTickPositions(tickPositions || DEFAULT_TICK_POSITION);
      setExtraSensorData([]);

      return { identicalStringsList, newData };
    }
    return { identicalStringsList: [], newData: [] };
  }, [motionHeatmapApiData, switchMotionHeatmapChartView]);

  const motionHeatmapLocationData: HeatmapModel = useMemo(() => {
    if (
      motionHeatmapApiData &&
      switchMotionHeatmapChartView ===
      MotionHeatmapChartType.Motion_Count_Vs_Location
    ) {
      const extractedData = motionHeatmapApiData.map((item: any) => [
        item.location,
        item.hour,
        item.motionCount,
      ]);

      const { identicalStringsList, newData, tickPositions } =
        modifyDataForHeatmapLocation(extractedData);

      setTickPositions(tickPositions || DEFAULT_TICK_POSITION);
      setExtraSensorData([]);

      return { identicalStringsList, newData };
    }
    return { identicalStringsList: [], newData: [] };
  }, [motionHeatmapApiData, switchMotionHeatmapChartView]);

  const [motionDataLoaded, setMotionDataLoaded] = useState(0);

  useEffect(() => {
    setMotionDataLoaded(motionDataLoaded + 1);
  }, [motionHeatmapLevelData]);

  // M-HM chart

  const [motionHeatmapVsLevelValues, setMotionHeatmapVsLevelValues] =
    useState<any>({
      min: 0,
      max: 0,
    });

  const [motionHeatmapVsSensorValues, setMotionHeatmapVsSensorValues] =
    useState<any>({
      min: 0,
      max: 0,
    });

  const { data: motionHeatmapListByModule } = useQuery({
    queryKey: ["getBaselineListByModuleId", 3, blockCode],
    queryFn: () =>
      getBaselineListByModuleId({
        baselineModuleId: 3,
        blockCode: blockCode,
        assetCode: null,
      }),
  });

  useEffect(() => {
    if (motionHeatmapListByModule?.data) {
      motionHeatmapListByModule?.data?.map((module: any, ind: number) => {
        if (ind == 0) {
          setMotionHeatmapVsLevelValues({
            min: module.motionLocationMinValue,
            max: module.motionLocationMaxValue,
          });

          setMotionHeatmapVsSensorValues({
            min: module.motionSensorMinValue,
            max: module.motionSensorMaxValue,
          });

          setIsBaselineLoading(false);
        }
      });
    }
  }, [motionHeatmapListByModule]);

  useEffect(() => {
    dispatch(
      getassetDisplayLevelAndLocationsByBlock({
        blockCode,
        chartType: AssetDisplayLevelChartType.Motion,
      })
    ).then((response: any) => {
      setAssetDisplayLevelsByBlockData(response?.payload);
      setIsLoadingAssetDisplayLevelAndLocationsByBlock(false);
    }).catch((e: any) => {
      setIsLoadingAssetDisplayLevelAndLocationsByBlock(false);
      console.log("Error fetching asset display levels and locations by block: ", e);
    });
  }, [blockCode]);

  useEffect(() => {
    if (activeKey === "Sensor") {
      setSwitchMotionHeatmapChartView(
        MotionHeatmapChartType.Motion_Count_Vs_Sensor
      );
      // Keep redirectChart unchanged — persist the last Level/Location value

    } else if (activeKey === "Location") {
      setSwitchMotionHeatmapChartView(
        MotionHeatmapChartType.Motion_Count_Vs_Location
      );
      setRedirectChart("DISPLAYLOCATION");
    } else {
      setSwitchMotionHeatmapChartView(
        MotionHeatmapChartType.Motion_Count_Vs_Level
      );
      setRedirectChart("LEVEL");
    }
    setRender(render + 1);
  }, [activeKey]);

  const onChangeHandler = (selectedValue: any) => {
    setSelectedLocation(selectedValue === "All Level" ? null : selectedValue);
  };

  // Responsive placeholder for tabular filter
  const [responsivePlaceholder, setResponsivePlaceholder] = useState(
    window.innerWidth <= 992 ? "Select" : "Select Levels/Locations"
  );

  // Responsive style for tabular filter
  const [tabularFilterStyle, setTabularFilterStyle] = useState<
    CSSProperties | undefined
  >(undefined);

  // Update placeholder and style based on window width
  useEffect(() => {
    const updatePlaceholder = () => {
      if (window.innerWidth <= 992) {
        setResponsivePlaceholder("Select");
        setTabularFilterStyle({
          width: "15%",
          position: "absolute",
          zIndex: "999",
          top: "-50px",
          right: "0",
        });
      } else {
        setResponsivePlaceholder("Select Levels/Locations");
        setTabularFilterStyle({
          width: "22%",
          position: "absolute",
          zIndex: "999",
          top: "-50px",
          right: "0",
        });
      }
    };

    updatePlaceholder();
    window.addEventListener("resize", updatePlaceholder);

    return () => {
      window.removeEventListener("resize", updatePlaceholder);
    };
  }, []);

  return (
    <div className="flex justify-content-between mt-4">
      <div className="w-100">
        <div className="bg-card tab-parent-mhm">
          <div className="d-md-flex">
            <div className="mb-4 mb-md-0">
              <h4 className="h4">
                Motion Heatmap{" "}
                <span style={{ fontSize: "16px" }}>(Last Day)</span>
              </h4>
            </div>
          </div>

          <div className="custom_tab">
            {motionDataLoaded ? (
              <B2Tabs
                key={render}
                activeKey={activeKey}
                pills
                tabClassName={""}
                onSelect={(selectedId: any) => {
                  if (selectedId === "Sensor") {
                    setSwitchMotionHeatmapChartView(
                      MotionHeatmapChartType.Motion_Count_Vs_Sensor
                    );

                    setSelectedLocation(null);
                    setActiveKey("Sensor");
                  } else if (selectedId === "Location") {
                    setSwitchMotionHeatmapChartView(
                      MotionHeatmapChartType.Motion_Count_Vs_Location
                    );
                    setActiveKey("Location");
                  } else {
                    setSwitchMotionHeatmapChartView(
                      MotionHeatmapChartType.Motion_Count_Vs_Level
                    );
                    setActiveKey("Level");
                  }
                }}
                tab={[
                  {
                    content: (
                      <>
                        {activeKey === "Level" ? (
                          <>
                            <CustomSpinnerComponent
                              height={"400px"}
                              width={"100%"}
                              isLoading={
                                isLoadingMotionHeatmapApiData ||
                                isRefreshed ||
                                isBaselineLoading
                              }
                            >
                              <HeatmapHighchartComponent
                                chartTitle=""
                                seriesData={motionHeatmapLevelData}
                                // redirectRoute={`/dashboard/${btoa(
                                //   blockCode
                                // )}/floorplan/`}
                                tickPositions={tickPositions}
                                colorMinMax={motionHeatmapVsLevelValues}
                                xAxisTitle="Level"
                                yAxisTitle="Time(hour)"
                                motionDataLoaded={motionDataLoaded}
                                chartType={"Level"}
                                setSelectedLocation={setSelectedLocation} // setSelectedLocation
                                setActiveKey={setActiveKey}
                                fileName={generateFilenameWithTimestamp(
                                  "Motion Heatmap (Last Day)"
                                )}
                                chartName={"Motion Heatmap (Last Day)"}
                              />
                            </CustomSpinnerComponent>
                          </>
                        ) : null}
                      </>
                    ),
                    id: "Level",
                    title: "Motion vs Level",
                  },
                  {
                    content: (
                      <>
                        {activeKey === "Location" ? (
                          <>
                            <CustomSpinnerComponent
                              height={"400px"}
                              width={"100%"}
                              isLoading={
                                isLoadingMotionHeatmapApiData ||
                                isRefreshed
                              }
                            >
                              <HeatmapHighchartComponent
                                chartTitle=""
                                seriesData={motionHeatmapLocationData}
                                tickPositions={tickPositions}
                                colorMinMax={motionHeatmapVsLevelValues}
                                xAxisTitle="Location"
                                yAxisTitle="Time(hour)"
                                chartType={"Location"}
                                setSelectedLocation={setSelectedLocation} // setSelectedLocation
                                setActiveKey={setActiveKey}
                                fileName={generateFilenameWithTimestamp(
                                  "Motion Heatmap (Last Day)"
                                )}
                                chartName={"Motion Heatmap (Last Day)"}
                              />
                            </CustomSpinnerComponent>
                          </>
                        ) : null}
                      </>
                    ),
                    id: "Location",
                    title: "Motion vs Location",
                  },
                  {
                    content: (
                      <>
                        {activeKey === "Sensor" ? (
                          <>
                            <TabularFilterComponent
                              selectedValue={selectedLocation}
                              options={
                                isLoadingAssetDisplayLevelAndLocationsByBlock ? [
                                  {
                                    DisplayLevel: "Loading ...",
                                    Type: null,
                                  },
                                ] :
                                  assetDisplayLevelsByBlockData
                              }
                              onChangeHandler={onChangeHandler}
                              placeHolder={responsivePlaceholder}
                              style={tabularFilterStyle}
                            />
                            <CustomSpinnerComponent
                              height={"400px"}
                              width={"100%"}
                              isLoading={
                                isLoadingMotionHeatmapApiData ||
                                isRefreshed ||
                                (shouldWaitForLevels && (isLoadingAssetDisplayLevelAndLocationsByBlock ||
                                  !assetDisplayLevelsByBlockData))
                              }
                            >
                              <HeatmapHighchartComponent
                                chartTitle=""
                                seriesData={motionHeatmapSensorData}
                                extraData={extraSensorData}
                                redirectRoute="/asset/"
                                tickPositions={tickPositions}
                                colorMinMax={motionHeatmapVsSensorValues}
                                xAxisTitle="Sensors"
                                yAxisTitle="Time(hour)"
                                chartType={"Sensor"}
                                setSelectedLocation={setSelectedLocation} // setSelectedLocation
                                setActiveKey={setActiveKey}
                                fileName={generateFilenameWithTimestamp(
                                  "Motion Heatmap (Last Day)"
                                )}
                                chartName={"Motion Heatmap (Last Day)"}
                              />
                            </CustomSpinnerComponent>
                          </>
                        ) : null}
                      </>
                    ),
                    id: "Sensor",
                    title: "Motion vs Sensor",
                  },
                ]}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MotionHeatmapChart;
